import {useCallback, useEffect, useMemo, useState} from "react";
import {CssBaseline} from "@mui/material";
import {ThemeProvider} from "@mui/material/styles";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {createKioskTheme} from "./theme";
import {useKioskSession} from "./context/KioskSessionContext";
import IdleWarningDialog from "./components/IdleWarningDialog";
import useIdleReset from "./hooks/useIdleReset";
import WelcomePage from "./pages/WelcomePage";
import ServiceModePage from "./pages/ServiceModePage";
import MenuPage from "./pages/MenuPage";
import CartPage from "./pages/CartPage";
import PaymentMethodPage from "./pages/PaymentMethodPage";
import LinePayPage from "./pages/LinePayPage";
import CompletePage from "./pages/CompletePage";
import {cancelKioskOrder} from "./api/kioskOrders";

function KioskRoutes({systemData}) {
  const navigate = useNavigate();
  const location = useLocation();
  const {state, resetSession} = useKioskSession();
  const [online, setOnline] = useState(navigator.onLine);
  const settings = systemData?.kiosk_settings ?? {};

  useEffect(() => {
    const connected = () => setOnline(true);
    const disconnected = () => setOnline(false);
    window.addEventListener("online", connected);
    window.addEventListener("offline", disconnected);
    return () => {
      window.removeEventListener("online", connected);
      window.removeEventListener("offline", disconnected);
    };
  }, []);

  const reset = useCallback(async () => {
    if (state.serverOrderId && state.paymentStatus !== "paid") {
      try {
        await cancelKioskOrder({orderId: state.serverOrderId, sessionId: state.sessionId});
      } catch {}
    }
    resetSession();
    navigate("/kiosk/welcome", {replace: true});
  }, [navigate, resetSession, state.paymentStatus, state.serverOrderId, state.sessionId]);

  const idleEnabled = state.stage !== "idle" && !["/kiosk/line-pay", "/kiosk/complete"].includes(location.pathname);
  const idle = useIdleReset({
    enabled: idleEnabled,
    idleSeconds: Number(settings.idle_seconds ?? 90),
    countdownSeconds: Number(settings.reset_countdown_seconds ?? 20),
    onReset: reset,
  });

  const pageProps = {systemData, online, onHome: reset};
  const pages = {
    "/kiosk/welcome": <WelcomePage {...pageProps}/>,
    "/kiosk/service": <ServiceModePage {...pageProps}/>,
    "/kiosk/menu": <MenuPage {...pageProps}/>,
    "/kiosk/cart": <CartPage {...pageProps}/>,
    "/kiosk/payment": <PaymentMethodPage {...pageProps}/>,
    "/kiosk/line-pay": <LinePayPage {...pageProps}/>,
    "/kiosk/complete": <CompletePage {...pageProps}/>,
  };

  return <>
    {pages[location.pathname] ?? <Navigate to="/kiosk/welcome" replace/>}
    <IdleWarningDialog open={idle.warningOpen} remaining={idle.remaining} onContinue={idle.continueSession} onCancel={reset}/>
  </>;
}

export default function KioskShell({systemData}) {
  const theme = useMemo(() => createKioskTheme(systemData?.prime_color), [systemData?.prime_color]);
  return <ThemeProvider theme={theme}>
    <CssBaseline/>
    <KioskRoutes systemData={systemData}/>
  </ThemeProvider>;
}
